import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MagnifyingGlass, Database, Download, FileCsv } from "@phosphor-icons/react";
import SearchForm from "@/components/SearchForm";
import RunHistory from "@/components/RunHistory";
import ProgressLog from "@/components/ProgressLog";
import ResultsTable from "@/components/ResultsTable";
import FiltersPanel from "@/components/FiltersPanel";
import MetricsPanel from "@/components/MetricsPanel";
import OutreachQueue from "@/components/OutreachQueue";
import { downloadCsv, csvFilenameFromRun } from "@/lib/csvDownload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FILTERS = {
    require_email: false,
    include_phone_only: true,
    skip_no_website: false,
    unique_domains: true,
    top_n: "",
    confidence: { High: true, Medium: true, Low: false },
};

export default function Home() {
    const [runs, setRuns] = useState([]);
    const [activeRunId, setActiveRunId] = useState(null);
    const [activeRun, setActiveRun] = useState(null);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const pollRef = useRef(null);

    const fetchRuns = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/runs`);
            setRuns(data);
        } catch (e) {
            console.error("runs list fail", e);
        }
    }, []);

    const fetchActive = useCallback(
        async (id) => {
            if (!id) return;
            try {
                const { data } = await axios.get(`${API}/runs/${id}`);
                setActiveRun(data);
                if (data.status === "complete" || data.status === "failed") {
                    fetchRuns();
                }
            } catch (e) {
                console.error("run detail fail", e);
            }
        },
        [fetchRuns],
    );

    useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    useEffect(() => {
        if (!activeRunId) return;
        fetchActive(activeRunId);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => {
            fetchActive(activeRunId);
        }, 1500);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [activeRunId, fetchActive]);

    useEffect(() => {
        if (activeRun && (activeRun.status === "complete" || activeRun.status === "failed")) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        }
    }, [activeRun]);

    const handleSearch = useCallback(
        async (payload) => {
            try {
                const { data } = await axios.post(`${API}/search`, payload);
                toast.success(`Search started: ${payload.category} / ${payload.location}`);
                setActiveRunId(data.id);
                setActiveRun({
                    ...data,
                    results: [],
                    logs: [],
                });
                fetchRuns();
            } catch (e) {
                const msg = e?.response?.data?.detail || e.message || "Search failed";
                toast.error(String(msg));
            }
        },
        [fetchRuns],
    );

    const handleSelectRun = useCallback((id) => {
        setActiveRunId(id);
    }, []);

    const handleDelete = useCallback(
        async (id) => {
            try {
                await axios.delete(`${API}/runs/${id}`);
                toast.success("Run deleted");
                if (id === activeRunId) {
                    setActiveRunId(null);
                    setActiveRun(null);
                }
                fetchRuns();
            } catch (e) {
                toast.error("Failed to delete run");
            }
        },
        [activeRunId, fetchRuns],
    );

    const handleResume = useCallback(
        async (id) => {
            try {
                const { data } = await axios.post(`${API}/runs/${id}/resume`);
                toast.success(
                    "Resuming — the crawl cache will skip providers already fetched",
                );
                setActiveRunId(data.id);
                setActiveRun({ ...data, results: [], logs: [] });
                fetchRuns();
            } catch (e) {
                const msg = e?.response?.data?.detail || e.message || "Failed to resume";
                toast.error(String(msg));
            }
        },
        [fetchRuns],
    );

    const filteredResults = useMemo(() => {
        if (!activeRun?.results) return [];
        const conf = filters.confidence;
        const allowed = new Set(
            Object.entries(conf)
                .filter(([, v]) => v)
                .map(([k]) => k),
        );
        const seen = new Set();
        const out = [];
        for (const r of activeRun.results) {
            let email = r.email || "";
            const phone = r.phone || "";
            const website = r.website || "";
            const confidence = r.confidence || "";

            if (email && confidence && !allowed.has(confidence)) {
                email = "";
            }
            if (filters.require_email && !email) continue;
            if (!filters.include_phone_only && !email && phone && !website) continue;
            if (filters.skip_no_website && !website) continue;
            if (filters.unique_domains && website) {
                try {
                    const host = new URL(website).host.replace(/^www\./, "");
                    if (seen.has(host)) continue;
                    seen.add(host);
                } catch {
                    // Intentionally silent: malformed website URLs just opt
                    // out of dedup. Surfacing a console.warn per row would
                    // flood the console for large result sets — the dedupe
                    // failure is functionally harmless.
                }
            }
            out.push({ ...r, email });
            if (filters.top_n && out.length >= Number(filters.top_n)) break;
        }
        return out;
    }, [activeRun, filters]);

    const buildCsvUrl = useCallback(() => {
        if (!activeRunId) return null;
        const params = new URLSearchParams();
        params.set("require_email", String(filters.require_email));
        params.set("include_phone_only", String(filters.include_phone_only));
        params.set("skip_no_website", String(filters.skip_no_website));
        params.set("unique_domains", String(filters.unique_domains));
        if (filters.top_n) params.set("top_n", String(filters.top_n));
        const levels = Object.entries(filters.confidence)
            .filter(([, v]) => v)
            .map(([k]) => k.toLowerCase())
            .join(",");
        params.set("confidence", levels || "high,medium");
        return `${API}/runs/${activeRunId}/export.csv?${params.toString()}`;
    }, [activeRunId, filters]);

    const handleDownload = useCallback(async () => {
        const url = buildCsvUrl();
        if (!url) return;
        try {
            const filename = csvFilenameFromRun(activeRun, "standard");
            const { rows } = await downloadCsv(url, filename);
            if (rows === 0) {
                toast.warning("CSV downloaded but contains 0 rows. Loosen the filters (confidence, require_email, unique_domains) and try again.");
            } else {
                toast.success(`Downloaded ${rows} row${rows === 1 ? "" : "s"} → ${filename}`);
            }
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail || e.message;
            if (status === 401) {
                toast.error("Session expired — please sign in again.");
            } else if (status === 404) {
                toast.error("This run no longer exists.");
            } else {
                toast.error(`CSV download failed: ${detail || "unknown error"}`);
            }
        }
    }, [buildCsvUrl, activeRun]);

    const isRunning =
        activeRun && (activeRun.status === "running" || activeRun.status === "queued");
    const hasResults = (activeRun?.results?.length ?? 0) > 0;

    return (
        <div className="min-h-screen bg-white">
            <div className="flex bg-grid min-h-[calc(100vh-4rem)]">
                {/* Sidebar */}
                <aside className="w-[260px] border-r border-[#e4e4e7] bg-white shrink-0 hidden lg:block">
                    <RunHistory
                        runs={runs}
                        activeId={activeRunId}
                        onSelect={handleSelectRun}
                        onDelete={handleDelete}
                        onResume={handleResume}
                    />
                </aside>

                {/* Main */}
                <main className="flex-1 p-6 lg:p-8 max-w-[1400px] mx-auto w-full">
                    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <div className="label-kicker mb-2">Extraction Configuration</div>
                            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                                Collect service provider contacts,
                                <br />
                                export to <span className="underline decoration-[3px]">CSV</span>.
                            </h1>
                            <p className="mt-3 text-[#52525b] text-sm max-w-xl">
                                Search yellow.co.nz + the open web by category and location, visit
                                provider websites, and extract publicly published emails and
                                phone numbers. Output a clean 4-column CSV.
                            </p>
                        </div>
                    </div>

                    {/* Search + Filters */}
                    <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6 mb-6">
                        <SearchForm onSubmit={handleSearch} isRunning={!!isRunning} />
                        <FiltersPanel
                            value={filters}
                            onChange={setFilters}
                            disabled={!hasResults}
                        />
                    </div>

                    {/* Progress + Log */}
                    {activeRun && (
                        <div className="mb-6 grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
                            <ProgressLog run={activeRun} />
                            <MetricsPanel run={activeRun} />
                        </div>
                    )}

                    {/* Results */}
                    <div className="panel">
                        <div className="panel-header">
                            <div className="flex items-center gap-3">
                                <FileCsv size={18} weight="bold" />
                                <span className="label-kicker">Results Preview</span>
                                {activeRun && (
                                    <span className="mono text-xs text-[#52525b]">
                                        {filteredResults.length}/{activeRun.results?.length ?? 0}{" "}
                                        rows
                                    </span>
                                )}
                            </div>
                            <button
                                className="btn-primary"
                                onClick={handleDownload}
                                disabled={!hasResults}
                                data-testid="download-csv-btn"
                            >
                                <Download size={16} weight="bold" />
                                Download CSV
                            </button>
                        </div>
                        <ResultsTable rows={filteredResults} loading={!!isRunning && !hasResults} />
                    </div>

                    {/* Outreach Queue — persistent operational memory */}
                    {activeRun && activeRun.category && activeRun.location && (
                        <div className="mt-6" data-testid="outreach-section">
                            <OutreachQueue
                                category={activeRun.category}
                                location={activeRun.location}
                                runId={activeRun.id}
                            />
                        </div>
                    )}

                    {!activeRun && (
                        <div className="mt-8 panel p-10 text-center">
                            <MagnifyingGlass size={32} weight="thin" className="mx-auto mb-4 text-[#a1a1aa]" />
                            <div className="label-kicker mb-2">Ready</div>
                            <div className="font-display text-2xl font-bold">
                                Enter a category and location to begin
                            </div>
                            <div className="text-sm text-[#52525b] mt-2">
                                Try <span className="mono">Plumbing / Auckland</span> or
                                <span className="mono"> Electrician / Christchurch</span>
                            </div>
                        </div>
                    )}

                    <footer className="mt-10 pt-6 border-t border-[#e4e4e7] text-xs text-[#52525b] flex items-center justify-between flex-wrap gap-2">
                        <span>
                            Only publicly listed contact data is collected. You are responsible
                            for lawful outreach and data use.
                        </span>
                        <span className="mono">v0.1 · standalone</span>
                    </footer>
                </main>
            </div>
        </div>
    );
}
